import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import authService from '../services/authService';
import { usePageTitle } from '../hooks/usePageTitle';
import { PasswordStrength, SuccessCheck } from '../components/ui';
import { ProfilUnterseite } from '../components/profil/ProfilUnterseite';

/** Dieselbe Grenze wie `UserService.validatePassword` im Backend. */
const MIN_LAENGE = 10;

const ChangePassword: React.FC = () => {
  usePageTitle('Passwort ändern');
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.newPassword.length < MIN_LAENGE) {
      toast.error(`Das Passwort muss mindestens ${MIN_LAENGE} Zeichen lang sein`);
      return;
    }

    setIsSubmitting(true);

    try {
      await authService.changePassword({
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      });
      toast.success('Passwort geändert');
      setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        navigate('/profile');
      }, 1500);
    } catch (error: any) {
      toast.error(error.message || 'Passwort konnte nicht geändert werden');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ProfilUnterseite titel="Passwort ändern">
      <form className="pf-gruppe" onSubmit={handleSubmit}>
        <label className="pf-feld">
          <span className="pf-feld-label">Aktuelles Passwort</span>
          <input className="pf-eingabe" type="password" required autoComplete="current-password"
            value={formData.oldPassword} disabled={isSubmitting}
            onChange={(e) => setFormData({ ...formData, oldPassword: e.target.value })} />
        </label>
        <label className="pf-feld">
          <span className="pf-feld-label">Neues Passwort</span>
          <input className="pf-eingabe" type="password" required minLength={MIN_LAENGE} autoComplete="new-password"
            value={formData.newPassword} disabled={isSubmitting} placeholder={`Mindestens ${MIN_LAENGE} Zeichen`}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })} />
          <PasswordStrength password={formData.newPassword} />
        </label>
        <label className="pf-feld">
          <span className="pf-feld-label">Neues Passwort wiederholen</span>
          <input className="pf-eingabe" type="password" required minLength={MIN_LAENGE} autoComplete="new-password"
            value={formData.confirmPassword} disabled={isSubmitting}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} />
          {formData.confirmPassword && formData.newPassword && (
            <span className="pf-feld-hilfe" style={{ fontWeight: 600, color: formData.confirmPassword === formData.newPassword ? '#1B7A4B' : '#A82F5A' }}>
              {formData.confirmPassword === formData.newPassword ? 'Passwörter stimmen überein' : 'Passwörter stimmen nicht überein'}
            </span>
          )}
        </label>
        <button type="submit" className="pf-knopf" disabled={isSubmitting || showSuccess}>
          {showSuccess ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <SuccessCheck size={18} /> Gespeichert
            </span>
          ) : isSubmitting ? 'Wird geändert …' : 'Passwort speichern'}
        </button>
      </form>
      <p className="pf-hinweis">Mindestens {MIN_LAENGE} Zeichen, am besten mit Zahl und Sonderzeichen.</p>
    </ProfilUnterseite>
  );
};

export default ChangePassword;
